import { isAxiosError } from 'axios';
import { computed, onScopeDispose, ref, type Ref, watch } from 'vue';
import {
  getRunnersMonitoring,
  type MonitoringRange,
  type RunnerMonitoringBatch,
  type RunnerMonitoringEntry,
} from '@/api/runners';
import {
  availableRangesOf,
  DEFAULT_MONITORING_RANGE,
  isMonitoringRange,
  normalizeRange,
} from '@/lib/monitoring-format';

const RANGE_STORAGE_KEY = 'runner-dashboard-monitoring-range';

export const METRICS_INTERVAL_MS = 60_000;
export const METRICS_JITTER_MS = 10_000;

export function loadMonitoringRange(): MonitoringRange {
  try {
    const stored = localStorage.getItem(RANGE_STORAGE_KEY);
    if (isMonitoringRange(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_MONITORING_RANGE;
}

export function saveMonitoringRange(range: MonitoringRange): void {
  try {
    localStorage.setItem(RANGE_STORAGE_KEY, range);
  } catch {
    /* quota exceeded etc. */
  }
}

export interface RunnerMonitoringView {
  status: 'loading' | 'paused' | 'unavailable' | 'ready';
  entry: RunnerMonitoringEntry | null;
  range: MonitoringRange;
  from: number;
  to: number;
  bucketSeconds: number;
  now: number;
}

export interface RunnerMetricsOptions {
  intervalMs?: number;
  jitterMs?: number;
  fetch?: (range: MonitoringRange) => Promise<RunnerMonitoringBatch>;
}

/**
 * Periodically loads the monitoring batch while `enabled` is true and the tab is
 * visible. Call `start()` once mounted; polling stops when the scope is disposed.
 * Change the range with `select()` so that only explicit choices are persisted.
 */
export function useRunnerMetrics(
  enabled: Ref<boolean>,
  range: Ref<MonitoringRange>,
  options: RunnerMetricsOptions = {}
) {
  const intervalMs = options.intervalMs ?? METRICS_INTERVAL_MS;
  const jitterMs = options.jitterMs ?? METRICS_JITTER_MS;
  const fetchBatch = options.fetch ?? getRunnersMonitoring;

  const batch = ref<RunnerMonitoringBatch | null>(null);
  const subscriptionPaused = ref(false);
  const failed = ref(false);
  const now = ref(Date.now());

  const entries = computed(() => {
    const map = new Map<string, RunnerMonitoringEntry>();
    for (const entry of batch.value?.runners ?? []) map.set(String(entry.runnerId), entry);
    return map;
  });

  const availableRanges = computed(() => availableRangesOf(batch.value?.availableRanges));

  let started = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight = false;
  let refetchAfterFlight = false;

  function canRun() {
    return started && enabled.value && !document.hidden;
  }

  function clearTimer() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function schedule() {
    clearTimer();
    if (!canRun()) return;
    // Jitter spreads requests from many open dashboards over time.
    const delay = intervalMs + (Math.random() * 2 - 1) * jitterMs;
    timer = setTimeout(() => {
      timer = null;
      void refresh();
    }, delay);
  }

  async function refresh(): Promise<void> {
    if (!canRun()) return;
    if (inFlight) return;
    clearTimer();
    inFlight = true;
    const requested = range.value;
    try {
      const data = await fetchBatch(requested);
      if (requested === range.value) {
        batch.value = data;
        subscriptionPaused.value = false;
        failed.value = false;
        // Not persisted, so the stored choice is tried again on the next dashboard visit.
        range.value = normalizeRange(requested, availableRangesOf(data.availableRanges));
      }
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        subscriptionPaused.value = true;
        batch.value = null;
        failed.value = false;
      } else if (requested === range.value) {
        failed.value = true;
      }
    } finally {
      inFlight = false;
      now.value = Date.now();
    }
    if (refetchAfterFlight || requested !== range.value) {
      refetchAfterFlight = false;
      await refresh();
    } else {
      schedule();
    }
  }

  // Range changes are caught by the post-flight range check; queueing them as well would
  // duplicate the follow-up request.
  function refreshOnResume() {
    if (inFlight) refetchAfterFlight = true;
    else void refresh();
  }

  function onVisibilityChange() {
    if (document.hidden) clearTimer();
    else refreshOnResume();
  }

  function start() {
    if (started) return;
    started = true;
    document.addEventListener('visibilitychange', onVisibilityChange);
    void refresh();
  }

  function stop() {
    started = false;
    clearTimer();
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  watch(enabled, (value) => {
    if (value) refreshOnResume();
    else clearTimer();
  });

  watch(range, () => {
    failed.value = false;
    void refresh();
  });

  function select(value: MonitoringRange) {
    saveMonitoringRange(value);
    range.value = value;
  }

  onScopeDispose(stop);

  function viewFor(runnerId: string): RunnerMonitoringView {
    // A batch for a previously selected range must not pass as the current one.
    const data = batch.value?.range === range.value ? batch.value : null;
    let status: RunnerMonitoringView['status'] = 'ready';
    if (subscriptionPaused.value) status = 'paused';
    else if (!data) status = failed.value ? 'unavailable' : 'loading';
    return {
      status,
      entry: data ? (entries.value.get(String(runnerId)) ?? null) : null,
      range: data?.range ?? range.value,
      from: data ? Date.parse(data.from) : 0,
      to: data ? Date.parse(data.to) : 0,
      bucketSeconds: data?.bucketSeconds ?? 0,
      now: now.value,
    };
  }

  return {
    batch,
    availableRanges,
    subscriptionPaused,
    failed,
    now,
    start,
    stop,
    refresh,
    select,
    viewFor,
  };
}
